import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCaretDown, faCaretUp } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { debounce } from 'lodash'
import { FC, useCallback, useMemo, useState } from 'react'

export type MultiSelectFilterPros = {
  options: { value: string; label: string }[]
  value: string[]
  label: string
  onChange: (values: string[]) => void
  width?: number
  debounceTime?: number
}

const MultiSelectFilter: FC<MultiSelectFilterPros> = ({
  options,
  value,
  label,
  onChange,
  width = 200,
  debounceTime = 1000,
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [selectedValues, setSelectedValues] = useState<string[]>(value)

  const debouncedOnChange = useMemo(() => debounce(onChange, debounceTime), [onChange, debounceTime])

  const handleChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const selectedValues = e.target.value as string[]
      setSelectedValues(selectedValues)
      debouncedOnChange(selectedValues)
    },
    [debouncedOnChange]
  )

  return (
    <FormControl sx={{ width: width }}>
      <InputLabel size="small" variant="outlined" id={`${label}-label`}>
        {label}
      </InputLabel>
      <Select
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        fullWidth
        labelId={`multiselect-label`}
        multiple
        value={selectedValues}
        onChange={handleChange}
        IconComponent={(props) => (
          <FontAwesomeIcon
            {...props}
            icon={(isFocused ? faCaretUp : faCaretDown) as IconProp}
            style={{ marginRight: '12px', fontSize: '24px' }}
            onClick={props.onClick}
          />
        )}
        input={<OutlinedInput notched={!!value?.length || isFocused} label={label} style={{ overflow: 'hidden' }} />}
        renderValue={(selected) =>
          options
            .filter((o) => selected.includes(o.value))
            .map((s) => s.label)
            .join(', ')
        }
        variant="outlined"
        sx={{ height: 42 }}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedValues.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default MultiSelectFilter
